import { shuffle } from 'lodash';
import { BLANK, splitVariants } from '../QuestionsLearning/questionTypes';

// Answer drafts per question type:
// type1 string · type2 [id] · type3 [id] in chosen order · type4 {variantId: groupIndex}
// type5 [string] per blank · type6 {slotId: variantId} · type7 {pairId: chosenPairId}

const norm = (value) => String(value || '').toLowerCase().trim();

const accepts = (variants, value) => splitVariants(variants).map(norm).includes(norm(value));

export const blanksCount = (question) => String(question.answers?.value || '').split(BLANK).length - 1;

export const allVariants = (question) => (question.answers || []).flatMap(group => group.variants || []);

export const emptyDraft = (question) => {
  switch (question.question_type) {
    case 'type1':
      return '';
    case 'type2':
    case 'type3':
    case 'type5':
      return [];
    default:
      return {};
  }
};

// Shuffled order of the things a learner picks from, fixed per attempt
export const prepareOrder = (question) => {
  const answers = question.answers;
  switch (question.question_type) {
    case 'type2':
    case 'type3':
    case 'type7':
      return shuffle((answers || []).map(_a => _a._id));
    case 'type4':
      return shuffle(allVariants(question).map(_v => _v._id));
    case 'type6':
      return shuffle((answers?.variants || []).map(_v => _v._id));
    default:
      return [];
  }
};

export const isComplete = (question, draft) => {
  const answers = question.answers;
  switch (question.question_type) {
    case 'type1':
      return Boolean(norm(draft));
    case 'type2':
      return draft.length > 0;
    case 'type3':
      return draft.length === (answers || []).length;
    case 'type4':
      return Object.keys(draft).length === allVariants(question).length;
    case 'type5': {
      const count = blanksCount(question);
      return count > 0 && Array.from({ length: count }).every((_v, i) => Boolean(norm(draft[i])));
    }
    case 'type6':
      return Object.keys(draft).length === (answers?.variants || []).length;
    case 'type7':
      return Object.keys(draft).length === (answers || []).length;
    default:
      return false;
  }
};

// ─── Per item checks (used both for grading and for highlighting after "Check") ───

export const orderItemCorrect = (question, draft, index) => {
  const expected = (question.answers || [])[index];
  const actual = (question.answers || []).find(_a => _a._id === draft[index]);
  if (!expected || !actual) return false;
  return expected._id === actual._id || (norm(expected.text) === norm(actual.text) && expected.url === actual.url);
};

export const categoryItemCorrect = (question, variantId, groupIndex) => (
  ((question.answers || [])[groupIndex]?.variants || []).some(_v => _v._id === variantId)
);

export const blankCorrect = (question, draft, index) => accepts((question.answers?.variants || [])[index], draft[index]);

export const slotCorrect = (question, draft, slot) => {
  const placed = (question.answers?.variants || []).find(_v => _v._id === draft[slot._id]);
  return Boolean(placed) && (placed._id === slot._id || norm(placed.value) === norm(slot.value));
};

export const pairCorrect = (question, draft, pair) => {
  const chosen = (question.answers || []).find(_p => _p._id === draft[pair._id]);
  return Boolean(chosen) && (chosen._id === pair._id || norm(chosen.answer) === norm(pair.answer));
};

export const isCorrect = (question, draft) => {
  const answers = question.answers;
  switch (question.question_type) {
    case 'type1':
      return accepts(answers, draft);
    case 'type2': {
      const correct = (answers || []).filter(_a => _a.isTrue).map(_a => _a._id);
      return correct.length === draft.length && correct.every(id => draft.includes(id));
    }
    case 'type3':
      return (answers || []).every((_a, i) => orderItemCorrect(question, draft, i));
    case 'type4':
      return allVariants(question).every(_v => categoryItemCorrect(question, _v._id, draft[_v._id]));
    case 'type5':
      return Array.from({ length: blanksCount(question) }).every((_v, i) => blankCorrect(question, draft, i));
    case 'type6':
      return (answers?.variants || []).every(slot => slotCorrect(question, draft, slot));
    case 'type7':
      return (answers || []).every(pair => pairCorrect(question, draft, pair));
    default:
      return false;
  }
};
